if (!customElements.get('stat-counter')) {
  class StatCounter extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.stack-stats__item'));
      this.valueEls = Array.from(this.querySelectorAll('.stack-stats__value'));
      if (this.valueEls.length === 0) return;

      this.statDuration = parseInt(this.dataset.duration, 10) || 1600;
      this.holdDuration = parseInt(this.dataset.holdDuration, 10) || 900;
      this.loopPause = parseInt(this.dataset.loopPause, 10) || 1200;
      this._stopped = false;

      this.valueEls.forEach((el) => {
        const parsed = StatCounter.parseValue(el.dataset.value);
        if (parsed === null) return;
        el.statTarget = parsed.value;
        el.statDecimals = parsed.decimals;
      });

      this.setAllToFinal();

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduceMotion || !('IntersectionObserver' in window)) {
        this.items.forEach((item) => item.classList.add('is-active'));
        this.setAttribute('loaded', '');
        return;
      }

      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.observer.disconnect();
          this.runLoop();
        }
      }, { threshold: 0.35 });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this._stopped = true;
      if (this.observer) this.observer.disconnect();
    }

    showAllFinal() {
      this.items.forEach((item) => item.classList.add('is-active'));
      this.setAllToFinal();
    }

    setAllToFinal() {
      this.valueEls.forEach((el) => {
        if (el.statTarget === undefined) return;
        el.textContent = StatCounter.format(el.statTarget, el.statDecimals);
      });
    }

    setActiveIndex(index) {
      this.items.forEach((item, i) => {
        item.classList.toggle('is-active', i === index);
        if (i !== index) item.classList.remove('is-animating');
      });
    }

    wait(ms) {
      return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
      });
    }

    animateStat(index) {
      const el = this.valueEls[index];
      if (!el || el.statTarget === undefined) return Promise.resolve();

      const item = this.items[index];

      this.setAllToFinal();
      this.setActiveIndex(index);
      item.classList.add('is-animating');
      el.textContent = StatCounter.format(0, el.statDecimals);

      return new Promise((resolve) => {
        const start = performance.now();

        const tick = (now) => {
          if (this._stopped) {
            item.classList.remove('is-animating');
            resolve();
            return;
          }

          const progress = Math.min((now - start) / this.statDuration, 1);
          const eased = 1 - (1 - progress) ** 3;
          el.textContent = StatCounter.format(el.statTarget * eased, el.statDecimals);

          if (progress < 1) {
            requestAnimationFrame(tick);
          } else {
            item.classList.remove('is-animating');
            resolve();
          }
        };

        requestAnimationFrame(tick);
      });
    }

    async runLoop() {
      while (!this._stopped) {
        for (let i = 0; i < this.valueEls.length; i += 1) {
          if (this._stopped) return;
          await this.animateStat(i);
          if (this._stopped) return;
          await this.wait(this.holdDuration);
        }

        if (this._stopped) return;
        await this.wait(this.loopPause);
        this.setAllToFinal();
        this.items.forEach((item) => item.classList.remove('is-active'));
      }
    }

    static parseValue(raw) {
      if (!raw) return null;
      const cleaned = raw.replace(/,/g, '').trim();
      const value = parseFloat(cleaned);
      if (Number.isNaN(value)) return null;
      const decimalPart = cleaned.split('.')[1];
      return { value, decimals: decimalPart ? decimalPart.length : 0 };
    }

    static format(value, decimals) {
      return value.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      });
    }
  }

  customElements.define('stat-counter', StatCounter);
}
